import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Flex, Input, MenuButton, PopupProps, Tooltip } from "@fluentui/react-northstar";
import {
    CalendarIcon, SearchIcon, FilterIcon, RetryIcon, ComposeIcon, PresenceAvailableIcon, PresenceStrokeIcon

} from "@fluentui/react-icons-northstar";
import filter from "lodash/filter";
import ReportList from "../components/reportList";
import Alert from "../components/common/alert";
import IReport from "../interfaces/IReport";
import HiddenReportsMessage from "../components/hiddenReportsMessage";

import { useApp } from "../contexts/appContext";
import { useCustomers, useProjects, useReports } from "../queries";
import { getErrorMessage, isAuthIssue } from "../queries/queryClient";
import styles from "../common.module.scss";
import { addDays } from "../common/dateOperations";

const Reports: React.FunctionComponent<{}> = () => {
    document.title = "Home";
    const history = useHistory();
    const { state: appState, dispatch } = useApp();
    const { backgroundColor, onlyActiveReports, reportFilter } = appState;

    const [query, setQuery] = useState(appState.reportsQuery);
    const [showFilterTooltip, setShowFilterTooltip] = useState<boolean | undefined>();

    const { data: reportsData, error, refetch } = useReports();
    const reports: IReport[] = useMemo(() => reportsData ?? [], [reportsData]);

    // Loaded here as well so that the report form opens with the pickers already filled
    useProjects();
    useCustomers();

    useEffect(() => {
        dispatch({ type: "SET_REPORTS_QUERY", query });
    }, [query]);

    const filteredReports = useMemo(() => {
        let filtered = reports;
        if (query) {
            const lower = query.toLowerCase();
            const words = lower.split(" ");

            filtered = filter(filtered, (x: IReport) => {
                const subject = x.subject.toLowerCase();
                const project = x.project.subject.toLowerCase();

                // tslint:disable-next-line: no-shadowed-variable
                const match: boolean[] = words.map((_: any) => false);
                for (let index = 0; index < words.length; index++) {
                    match[index] = subject.indexOf(words[index]) > -1 || project.indexOf(words[index]) > -1;
                }

                return match.filter((m: boolean) => m).length === match.length;
            });
        }

        if (onlyActiveReports) {
            filtered = filter(filtered, (x: IReport) => x.state !== 1);
        }

        const today = addDays(new Date(), 1);
        today.setHours(0, 0, 0, 0);
        if (reportFilter === "FUTURE") {
            filtered = filter(filtered, (x: IReport) => new Date(x.start) <= today);
        } else if (reportFilter === "LEAVE") {
            const badOnes = filter(filtered, (x: IReport) => new Date(x.start) > today &&
                (
                    x.project.subject === "ADMIN-TSK-Placená dovolená" ||
                    x.project.subject === "ADMIN-TSK-Neplacené volno" ||
                    x.project.subject === "ADMIN-TSK-Branná povinnost" ||
                    x.project.subject === "ADMIN-TSK-Blokace"
                )
            ).map((x: IReport) => x.id);
            filtered = filter(filtered, (x: IReport) => !badOnes.includes(x.id));
        }

        return filtered;
    }, [query, reports, onlyActiveReports, reportFilter]);

    const toggleOnlyActive = () => {
        dispatch({ type: "SET_ONLY_ACTIVE_REPORTS", onlyActiveReports: !onlyActiveReports });
    }

    const setReportFilter = (value: "ALL" | "FUTURE" | "LEAVE") => {
        dispatch({ type: "SET_REPORT_FILTER", reportFilter: value });
    }

    const renderReportFilterIcon = (value: "ALL" | "FUTURE" | "LEAVE") =>
        reportFilter === value ? <PresenceAvailableIcon /> : <PresenceStrokeIcon />;

    return (
        <div>
            <div className={styles.centerContainer} style={{ minHeight: 100 }}>
                <Flex className={styles.stickySearch} style={{ backgroundColor }} column>
                    <Input
                        value={query} placeholder="Vyhledávání" onChange={(_: any, data: any) => setQuery(data.value)}
                        icon={<SearchIcon />} style={{ padding: "1em" }}
                        clearable fluid
                    />
                    <HiddenReportsMessage nrOfHiddenReports={reports.length - filteredReports.length} />
                </Flex>
                {error && <Alert type="error" authIssue={isAuthIssue(error)}>{getErrorMessage(error)}</Alert>}
                <ReportList items={filteredReports} />
            </div>

            <div className={styles.footer} style={{ background: backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    <Tooltip
                        trigger={<Button text primary={onlyActiveReports} iconOnly icon={<CalendarIcon />} onClick={toggleOnlyActive} />}
                        content="Zobrazit jen otevřené výkazy"
                    />
                    <Tooltip
                        content="Filtr"
                        open={showFilterTooltip}
                        trigger={
                            <MenuButton
                                onOpenChange={(_: any, data: PopupProps | undefined) => setShowFilterTooltip(data?.open ? false : undefined)}
                                trigger={<Button text iconOnly icon={<FilterIcon />} primary={reportFilter && reportFilter !== "ALL"} />}
                                menu={[
                                    {
                                        key: "all",
                                        content: "Zobrazit všechny výkazy",
                                        icon: renderReportFilterIcon("ALL"),
                                        onClick: () => setReportFilter("ALL")
                                    },
                                    {
                                        key: "future",
                                        content: "Skrýt budoucí výkazy",
                                        icon: renderReportFilterIcon("FUTURE"),
                                        onClick: () => setReportFilter("FUTURE")
                                    },
                                    {
                                        key: "future-leave",
                                        content: "Skrýt budoucí dovolené",
                                        icon: renderReportFilterIcon("LEAVE"),
                                        onClick: () => setReportFilter("LEAVE")
                                    }
                                ]}
                            />
                        }
                    />

                    <Tooltip
                        trigger={<Button text iconOnly icon={<RetryIcon />} onClick={() => refetch()} />}
                        content="Aktualizovat seznam výkazů"
                    />

                    <Flex.Item push>
                        <Button content="Nový" primary icon={<ComposeIcon />} onClick={() => history.push("/report/new")} />
                    </Flex.Item>
                </Flex>
            </div>
        </div>
    );
};

export default Reports;
